// BoardViewColumn.jsx

import React, { useCallback } from 'react';

// CSS
import './BoardViewColumn.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import BoardViewCard from 'views/board/components/BoardViewCard';

/**
 * BoardViewColumn
 *
 * @returns {ReactNode} The rendered component.
 */
const BoardViewColumn = ({
    column,
    items,
    otherKey,
    dragOverTarget,
    theme,
    handleInsertDragOver,
    handleInsertDrop,
    draggingNodeKey,
    handleCardClick,
    handleCardDoubleClick,
    handleCardDragStart,
    handleCardDragEnd,
    getCoverUrl,
    CoverComponent,
    cardFieldKeys,
    schemaFieldMap,
    schemaFieldMapLower,
    resolveFieldValue,
    formatFieldValue,
    language,
    nodeDragEnabled = false,
    typeDef = null,
    addTypeKey,
    onQuickActionCreateOnly,
    onQuickActionSelect,
    addButtonLabel,
    handleAddForColumn,
    isColumnDraggable,
    isColumnDragging,
    isColumnDropTarget,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    handleColumnDragEnd,
}) => {

    const isDropColumn = column.key !== otherKey;

    // ----- MEMOS ----- //

    const resolveInsertIndexFromEvent = useCallback((index, event) => {
        if (!event?.currentTarget || typeof index !== 'number') return index;
        const rect = event.currentTarget.getBoundingClientRect();
        const midpoint = rect.top + (rect.height / 2);
        return event.clientY >= midpoint ? index + 1 : index;
    }, []);

    // ----- HANDLERS ----- //

    const handleTargetedInsertDragOver = useCallback((insertIndex, event) => {
        event.stopPropagation();
        handleInsertDragOver(column.key, insertIndex, event);
    }, [column.key, handleInsertDragOver]);

    const handleTargetedInsertDrop = useCallback((insertIndex, event) => {
        event.stopPropagation();
        handleInsertDrop(column.key, insertIndex, event);
    }, [column.key, handleInsertDrop]);

    const handleColumnBodyDragOver = useCallback((event) => {
        if (!isDropColumn) return;
        handleInsertDragOver(column.key, items.length, event);
    }, [column.key, handleInsertDragOver, isDropColumn, items.length]);

    const handleColumnBodyDrop = useCallback((event) => {
        if (!isDropColumn) return;
        handleInsertDrop(column.key, items.length, event);
    }, [column.key, handleInsertDrop, isDropColumn, items.length]);

    const handleCardDragOver = useCallback((index, event) => {
        if (!isDropColumn) return;
        event.stopPropagation();
        const insertIndex = resolveInsertIndexFromEvent(index, event);
        handleInsertDragOver(column.key, insertIndex, event);
    }, [column.key, handleInsertDragOver, isDropColumn, resolveInsertIndexFromEvent]);

    const handleCardDrop = useCallback((index, event) => {
        if (!isDropColumn) return;
        event.stopPropagation();
        const insertIndex = resolveInsertIndexFromEvent(index, event);
        handleInsertDrop(column.key, insertIndex, event);
    }, [column.key, handleInsertDrop, isDropColumn, resolveInsertIndexFromEvent]);

    const isColumnBodyActiveDrop = isDropColumn
        && Boolean(draggingNodeKey)
        && dragOverTarget?.columnKey === column.key
        && dragOverTarget?.insertIndex === items.length;

    const headerTextColor = theme.foreground;
    const headerCountColor = column.color
        ? headerTextColor
        : theme.foregroundMuted;

    // ----- RENDER ----- //

    return (
        <div
          className="board-view-column"
          key={`board_col_${column.key}`}
        >
            <div
              className={`board-view-column-header${isColumnDraggable ? ' is-draggable' : ''}${isColumnDragging ? ' is-dragging' : ''}${isColumnDropTarget ? ' is-drop-target' : ''}`}
              style={{
                    backgroundColor: isColumnDropTarget ? theme.backgroundHover : theme.panel,
                    borderColor: isColumnDropTarget ? theme.foregroundMuted : theme.backgroundHover,
                }}
              draggable={isColumnDraggable}
              onDragEnd={isColumnDraggable ? handleColumnDragEnd : undefined}
              onDragOver={isColumnDraggable ? (event) => handleColumnDragOver(column, event) : undefined}
              onDragStart={isColumnDraggable ? (event) => handleColumnDragStart(column, event) : undefined}
              onDrop={isColumnDraggable ? (event) => handleColumnDrop(column, event) : undefined}
            >

                {/* COLUMN DRAG HANDLE */}
                {isColumnDraggable ? (
                    <div className="board-view-column-drag-handle" aria-hidden="true">
                        <Icon
                          iconKey="drag_indicator"
                          width={16}
                          height={16}
                          color={theme.foregroundMuted}
                        />
                    </div>
                ) : null}

                {/* COLUMN HEADER CIRCLE */}
                <div
                  className="board-view-column-header-circle"
                  style={{
                        backgroundColor: column.color ? column.color : 'transparent',
                    }}
                />

                {/* COLUMN HEADER ICON */}
                {column.iconKey ? (
                    <div className="board-view-column-header-icon" aria-hidden="true">
                        <Icon
                          iconKey={column.iconKey}
                          width={16}
                          height={16}
                          color={theme.foregroundMuted}
                        />
                    </div>
                ) : null}

                {/* COLUMN TITLE */}
                <div
                  className="board-view-column-title"
                  style={{
                    color: headerTextColor,
                  }}
                >
                    {column.label}
                </div>

                {/* COLUMN COUNT */}
                <div
                  className="board-view-column-count"
                  style={{
                    color: headerCountColor,
                  }}
                >
                    {items.length}
                </div>
            </div>
            <div
              className={`board-view-column-body${isColumnBodyActiveDrop ? ' is-active-drop' : ''}`}
              style={isColumnBodyActiveDrop ? { boxShadow: `inset 0 0 0 2px ${theme.foregroundMuted}` } : undefined}
              onDragOver={isDropColumn ? handleColumnBodyDragOver : undefined}
              onDrop={isDropColumn ? handleColumnBodyDrop : undefined}
            >

                {/* ADD BUTTON */}
                {addTypeKey && (onQuickActionCreateOnly || onQuickActionSelect) && (
                    <div className="board-view-column-add-button">
                        <Button
                          label={addButtonLabel}
                          backgroundColor={theme.backgroundMuted}
                          foregroundColor={theme.foreground}
                          hoverBackgroundColor={theme.buttonHoverBackgroundColor}
                          hoverForegroundColor={theme.buttonHoverLabelColor}
                          onClick={() => handleAddForColumn(column.key)}
                        />
                    </div>
                )}

                {items.map((node, index) => (
                    <React.Fragment key={node?.key}>

                        <BoardViewCard
                          node={node}
                          index={index}
                          columnKey={column.key}
                          isDropColumn={isDropColumn}
                          draggingNodeKey={draggingNodeKey}
                          dropPosition={dragOverTarget?.columnKey === column.key
                                ? (dragOverTarget?.insertIndex === index
                                    ? 'before'
                                    : (dragOverTarget?.insertIndex === index + 1 ? 'after' : null))
                                : null}
                          theme={theme}
                          handleCardClick={handleCardClick}
                          handleCardDoubleClick={handleCardDoubleClick}
                          handleCardDragStart={handleCardDragStart}
                          handleCardDragEnd={handleCardDragEnd}
                          handleCardDragOver={handleCardDragOver}
                          handleCardDrop={handleCardDrop}
                          getCoverUrl={getCoverUrl}
                          CoverComponent={CoverComponent}
                          cardFieldKeys={cardFieldKeys}
                          schemaFieldMap={schemaFieldMap}
                          schemaFieldMapLower={schemaFieldMapLower}
                          resolveFieldValue={resolveFieldValue}
                          formatFieldValue={formatFieldValue}
                          language={language}
                          nodeDragEnabled={nodeDragEnabled}
                          typeDef={typeDef}
                        />

                        {isDropColumn && (
                            <button
                              className={`board-view-insert-bar${dragOverTarget?.columnKey === column.key && dragOverTarget?.insertIndex === index ? ' is-active-drop' : ''}`}
                              style={{
                                    backgroundColor: dragOverTarget?.columnKey === column.key && dragOverTarget?.insertIndex === index
                                        ? theme.foregroundMuted
                                        : 'transparent',
                                }}
                              type="button"
                              aria-label="Move card here"
                              onDragOver={(event) => handleTargetedInsertDragOver(index, event)}
                              onDrop={(event) => handleTargetedInsertDrop(index, event)}
                            />
                        )}

                    </React.Fragment>
                ))}

                {!items.length && (
                    <div
                      className={`board-view-empty-card${isDropColumn && dragOverTarget?.columnKey === column.key && dragOverTarget?.insertIndex === 0 ? ' is-active-drop' : ''}`}
                      style={{
                            backgroundColor: theme.panel,
                            borderColor: theme.backgroundHover,
                            color: theme.foregroundMuted,
                        }}
                      aria-label={isDropColumn ? 'Move card into empty column' : undefined}
                      onDragOver={isDropColumn ? (event) => handleTargetedInsertDragOver(0, event) : undefined}
                      onDrop={isDropColumn ? (event) => handleTargetedInsertDrop(0, event) : undefined}
                    >
                        No cards
                    </div>
                )}

                {isDropColumn && (
                    <button
                      className={`board-view-insert-bar${dragOverTarget?.columnKey === column.key && dragOverTarget?.insertIndex === items.length ? ' is-active-drop' : ''}`}
                      style={{
                            backgroundColor: dragOverTarget?.columnKey === column.key && dragOverTarget?.insertIndex === items.length
                                ? theme.foregroundMuted
                                : 'transparent',
                        }}
                      type="button"
                      aria-label="Move card here"
                      onDragOver={(event) => handleTargetedInsertDragOver(items.length, event)}
                      onDrop={(event) => handleTargetedInsertDrop(items.length, event)}
                    />
                )}
            </div>
        </div>
    );
};

// Dev Tools
BoardViewColumn.displayName = 'BoardViewColumn';

export default React.memo(BoardViewColumn);
